/**
*+------------------------------------------------------------------------+
*| Licensed Materials - Property of IBM
*| IBM Cognos Products: Dashboard
*| (C) Copyright IBM Corp. 2020
*|
*| US Government Users Restricted Rights - Use, duplication or disclosure
*| restricted by GSA ADP Schedule Contract with IBM Corp.
*+------------------------------------------------------------------------+
*/
define([
	'underscore',
	'../../../apiHelpers/SlotAPIHelper'
], function(
	_,
	SlotAPIHelper
) {
	class VIPRDataPointSummarizer {
		//Helper used to provide summaries for VIPR entities (like legends/axis titles) to support tooltips etc.

		constructor (visualization) {
			this.slots = visualization && visualization.getSlots();
		}

		/**
		 * Summarize the datapoint values by tuple item
		 * This function will be called when a legend item or axis title is selected.
		 *
		 * @param viprDataPoints array of VIPRDataPoints to summarize
		 * @param itemIndex		dataitem index of the tuple class
		 * @param itemSource	tuple item source
		 */
		summarizeDataPointValues (viprDataPoints, itemIndex, itemSource) {
			// build data for the header (ie: captions for the selection  '2007 | Winter')
			let values = this._buildHeaderLine(itemSource, itemIndex);
			if (!viprDataPoints || !viprDataPoints.length || !this.slots) {
				//There are no points to summarize, just return the header.
				return values;
			}

			// Summarize the measure values for the points this tuple represents for this selection by measureId.
			// (eg: 10 points are involved when hovering on a legend.  Sum the measures for those 10 points (if appliable))
			let datapointsMap = {};

			const dataset = itemSource.dataset;
			if (dataset) {
				const slotList = this.slots.getMappedSlotList();
				const datasetSlotList = slotList.filter(slot => {
					return slot.getDefinition().getDatasetIdList().includes(dataset.id);
				});
				const multiMeasureAssignment = this._getMultiMeasureAssignment(datasetSlotList, itemSource);

				// Add values to corresponding measure in map
				for (let itemIdx = 0; itemIdx < dataset.getDataItemCount(); itemIdx++) {
					const viprDataItem = dataset.getDataItem(itemIdx);
					if (viprDataItem.getType()==='cont') {
						// Don't assume that the itemIdx is the same as the slot index
						// there could be a slot that contains multiple dataitems
						// We get the slot index from viprDataItem.indexes.dataitem
						let slotIndex = (viprDataItem.indexes && viprDataItem.indexes.dataitem);
						if (slotIndex == null) {
							// not sure if we would ever run into this.. but just in case we have scenarios where the slots index is not found
							slotIndex = itemIdx;
						}

						const slotAPI = datasetSlotList[slotIndex];
						const dataItemAPIs = slotAPI.getDataItemList();
						// Construct empty map with value slot data items
						slotAPI.getDataItemList().forEach(dataitemAPI => {
							const columnId = dataitemAPI.getColumnId();
							if (!datapointsMap[columnId]){
								datapointsMap[columnId] = {
									measureValues: [],
									aggregationType: dataitemAPI.getAggregation() || 'sum'
								};
							}
						});

						//Run through the datapoints, adding values to the map for this measure.
						viprDataPoints.forEach((viprDataPoint) => {
							const measureId = this._getMeasureIdFromDataPoint(viprDataPoint, dataItemAPIs, multiMeasureAssignment, slotAPI, itemIdx - slotIndex);

							if (measureId) {
								const viprValue = viprDataPoint.getValue(itemIdx);
								datapointsMap[measureId].measureValues.push(viprValue.getValue());
								datapointsMap[measureId].indexInResult = slotIndex;
							}
						});
					}
				}

				// Apply summaries for measure based on rules for aggregating.
				values=this._summarizeValuesInPointsMap(values, datapointsMap);
			}
			return values;
		}

		// Find where the _multiMeasuresSeries item is assigned and what tuples are to be displayed there
		//eg: {
		//		slotIndex: 2,  (the index of the Color slot in a bar chart for example)
		//		indexInSlot: 0,	(the _multiMeasuresSeries item is the first item in the color slot)
		//		multiMeasureItemCount: 2,
		//		tuplesInSlot: [VIPRTuple, VIPRTuple]
		// }
		_getMultiMeasureAssignment(slotList, itemSource) {
			let mmPlaceholderSlotIndex;
			let mmPlaceholderIndexInSlot;
			let numMMItems;
			const mmDataItem = _.find(slotList, (slotAPI, slotIdx) => {
				mmPlaceholderSlotIndex = slotIdx;
				let dataItems = slotAPI.getDataItemList() || [];
				if (SlotAPIHelper.isMultiMeasuresValueSlot(slotAPI)){
					numMMItems = dataItems.length;
				}
				return _.find(dataItems, (dataitemAPI, dataItemIdx) => {
					mmPlaceholderIndexInSlot = dataItemIdx;
					return dataitemAPI.getColumnId() === '_multiMeasuresSeries';
				});
			});
			const multiMeasureTuples = mmDataItem && itemSource && itemSource.dataset && itemSource.dataset.dataItems[mmPlaceholderSlotIndex].tuples;

			if (mmDataItem) {
				return {
					slotIndex: mmPlaceholderSlotIndex,	//What slot is the "measure group(n)" placeholder in?
					indexInSlot: mmPlaceholderIndexInSlot,	//Might be after other categoricals in that slot.
					tuplesInSlot: multiMeasureTuples,	//VIPRTuples this multimeasure represents
					multiMeasureItemCount: numMMItems	//A count of the multimeasure items in the value slot.
				};
			}
			return null;
		}

		_getMeasureIdFromDataPoint(viprDataPoint, dataItemAPIs, multiMeasureAssignment, slotAPI, dataItemIndex) {
			let measureId = null;
			if (dataItemAPIs.length > 1 && multiMeasureAssignment && SlotAPIHelper.isMultiMeasuresValueSlot(slotAPI)){
				//Find out what tuple in the placeholder slot we're processing, then pick out the measure columnId.
				//If Year and MeasureGroup(2) are assigned, mmTupleIdx might be
				//6 for tuple 2008-Revenue
				//7 for tuple 2008-Planned
				//multiMeasureAssignment.indexInSlot would be 1 (ie: the Measure item for that tuple).
				const mmTupleIdx = viprDataPoint.getTupleIndex(multiMeasureAssignment.slotIndex);
				const dataItemUId = multiMeasureAssignment.tuplesInSlot[mmTupleIdx].tuple[multiMeasureAssignment.indexInSlot].u;
				const dataItemAPI = _.find(dataItemAPIs, dataItemAPI => {
					return dataItemUId === dataItemAPI.getId();
				});
				measureId = dataItemAPI && dataItemAPI.getColumnId();
			} else if (!multiMeasureAssignment || (multiMeasureAssignment && (viprDataPoint.index % multiMeasureAssignment.multiMeasureItemCount === 0))) {
				//Condition 1: There are no multi measures items
				//Condition 2: There are multi measure items, but this measure isn't part of the multimeasure.
				//			Data rows have been interleaved 1 per measure but that means that OTHER measure values were
				//			duplicated.  We only want to add the other values once (so code only executes "% multiMeasureCount")
				measureId = dataItemAPIs[dataItemIndex].getColumnId();
			}
			return measureId;
		}

		_summarizeValuesInPointsMap(values, datapointsMap) {
			//Apply rules to combine the values collected by measure in the datapoints map.
			for (let measureId in datapointsMap) {
				const measureValues = datapointsMap[measureId].measureValues;
				const _aggregationType = datapointsMap[measureId].aggregationType;
				const idxInResult = datapointsMap[measureId].indexInResult;
				values[idxInResult] = values[idxInResult] || [];
				let summarized;
				if (measureValues.length > 1 && (_aggregationType === 'avg' || _aggregationType === 'countdistinct' || _aggregationType === 'calculated')) {
					summarized = 'N/A';	//can't support multi-select for avg or countdistinct.
				} else {
					summarized = measureValues[0]; // Keep first value to preserve single null value.
					for (let valueIdx = 1; valueIdx < measureValues.length; valueIdx++) {
						summarized += measureValues[valueIdx] || 0;
					}
				}

				values[idxInResult].push(summarized);
			}
			return values;
		}

		// build data for the header (ie: captions for the selection  '2007 | Winter')
		_buildHeaderLine(itemSource, itemIndex) {
			let values = [];
			if (typeof itemIndex !== 'undefined' && typeof itemSource !== 'undefined') {
				if (itemSource.tuple) {
					values[itemIndex] = itemSource.tuple;
				} else {
					let aValues = [];
					aValues.push({
						u: itemSource.getUniqueName(),
						d: itemSource.getCaption()
					});
					values[itemIndex] = aValues;
				}

				if (itemSource.getParent && itemSource.getParent()) {
					values[itemIndex][0].p = itemSource.getParent();
				}
			}
			return values;
		}

		/**
		 * NOTE: This code is only called when the config sets "shouldMultiPointSelectionSummarize"
		 * (which is only true for area charts)
		 * Summarize the datapoint targets to a single target
		 *
		 * ie. With the given 4 datapoint targets:
		 * Golf Equipment, Web, 100
		 * Camping Equipment, Web, 120,
		 * Outdoor Protection, Web, 90,
		 * Personal Accessories, Web, 140
		 *
		 * Should be summarized to:
		 * undefined, Web, 450
		 *
		 * @param prevTargets	array of multiple datapoint targets to summarize
		 */
		summarizeDataPointTargets (prevTargets) {
			var summary;
			var sumOrdIndex = -1;

			_.each(prevTargets, function(target) {
				if (!summary) {
					// clone the first target. Values will be modified so deep clone values.
					summary = { ...target };
					summary.values = JSON.parse(JSON.stringify(target.values));
				} else {
					// sum the remaining target to a single target
					if (sumOrdIndex < 0) {
						_.each(target.values, function(value, index) {
							// find the ordinal value index
							if (typeof value[0] === 'number') {
								sumOrdIndex = index;
							} else if (_.isEqual(_.pluck(summary.values[index], 'u'), _.pluck(value, 'u')) === false) {
								// Keep the repeating values but invalidate the detail values
								summary.values[index] = undefined;
							}
						});
					}
					// Add up the remaining datapoint ordinal values
					_.each(summary.values[sumOrdIndex], function(value, index) {
						if (target.values[sumOrdIndex] && target.values[sumOrdIndex][index]) {
							summary.values[sumOrdIndex][index] += target.values[sumOrdIndex][index];
						}
					});
				}
			});

			// return the summarized target as the sole target
			return [summary];
		}
	}

	return VIPRDataPointSummarizer;
});
